import Swal from 'sweetalert2';
import type { PlatformService } from '@/mocks/superAdminDashboard';

interface PlatformHealthPanelProps {
  services: PlatformService[];
}

const statusBadge: Record<string, { dot: string; label: string; labelColor: string }> = {
  online:   { dot: 'bg-emerald-500', label: 'Operativo', labelColor: 'text-emerald-600' },
  degraded: { dot: 'bg-amber-500 animate-pulse', label: 'Degradado', labelColor: 'text-amber-600' },
  offline:  { dot: 'bg-red-500 animate-pulse', label: 'Caído', labelColor: 'text-red-500' },
};

export default function PlatformHealthPanel({ services }: PlatformHealthPanelProps) {
  const onlineCount = services.filter((s) => s.status === 'online').length;
  const degradedCount = services.filter((s) => s.status === 'degraded').length;
  const offlineCount = services.filter((s) => s.status === 'offline').length;

  const handleTest = async (service: PlatformService) => {
    await Swal.fire({
      title: `Test — ${service.name}`,
      html: `<div style="text-align:left;font-size:13px">
        <p style="margin-bottom:6px"><strong>Servicio:</strong> ${service.name}</p>
        <p style="margin-bottom:6px"><strong>Estado actual:</strong> <span style="color:${service.status === 'online' ? '#10b981' : service.status === 'degraded' ? '#f59e0b' : '#ef4444'}">${statusBadge[service.status].label}</span></p>
        <p style="margin-bottom:6px"><strong>Último check:</strong> ${service.lastCheck}</p>
        <p style="margin-bottom:6px"><strong>Tiempo de respuesta:</strong> ${service.responseTime}</p>
        <p style="color:#6b7280;margin-top:8px">Ejecutando test de conectividad...</p>
        <div style="margin-top:12px;padding:8px 12px;background:#f0fdf4;border-radius:8px;border:1px solid #bbf7d0">
          <span style="color:#10b981;font-weight:600;font-size:12px">✓ Test completado — ${service.name} responde correctamente</span>
        </div>
      </div>`,
      confirmButtonText: 'Cerrar',
      confirmButtonColor: '#6b7280',
      customClass: {
        popup: 'rounded-2xl',
        title: 'font-bold text-sm',
        confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
      },
      buttonsStyling: false,
    });
  };

  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-background-200">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center">
            <i className="ri-heart-pulse-line text-foreground-500 text-sm"></i>
          </div>
          <div>
            <h2 className="text-foreground-950 font-semibold text-sm">Platform Health</h2>
            <p className="text-foreground-400 text-[10px]">Monitoreo de servicios en tiempo real</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
            <span className="text-foreground-400 text-[9px]">{onlineCount}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
            <span className="text-foreground-400 text-[9px]">{degradedCount}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-red-500"></span>
            <span className="text-foreground-400 text-[9px]">{offlineCount}</span>
          </div>
        </div>
      </div>

      {/* Service cards grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-px bg-background-200">
        {services.map((svc, idx) => {
          const badge = statusBadge[svc.status];
          return (
            <div
              key={svc.id}
              className="bg-background-50 p-4 hover:bg-background-100 transition-colors group"
              style={{ animation: `fadeInUp 0.3s ease-out forwards`, animationDelay: `${idx * 40}ms`, opacity: 0 }}
            >
              {/* Icon + status dot */}
              <div className="flex items-center justify-between mb-3">
                <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center">
                  <i className={`${svc.icon} text-foreground-500 text-sm`}></i>
                </div>
                <span className={`w-2.5 h-2.5 rounded-full ${badge.dot}`}></span>
              </div>

              {/* Name */}
              <p className="text-foreground-800 text-xs font-semibold mb-2">{svc.name}</p>

              {/* Status label */}
              <span className={`inline-block text-[9px] font-bold mb-2 ${badge.labelColor}`}>
                {badge.label}
              </span>

              {/* Metrics */}
              <div className="flex items-center gap-3 text-foreground-400 text-[9px] mb-3">
                <span className="flex items-center gap-1">
                  <i className="ri-time-line text-[9px]"></i>
                  {svc.lastCheck}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-speed-mini-line text-[9px]"></i>
                  {svc.responseTime}
                </span>
              </div>

              {/* Test button */}
              <button
                onClick={(e) => { e.stopPropagation(); handleTest(svc); }}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-background-100 text-foreground-500 text-[10px] font-semibold hover:bg-background-200 opacity-0 group-hover:opacity-100 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-flashlight-line text-[10px]"></i>
                Test
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}